import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import GroupIcon from '@mui/icons-material/Group';
import { useLiveQuizRoom } from '../../../hooks/useLiveQuizRoom';
import { joinRoom, submitLiveAnswer } from '../../../services/liveQuizService';
import { useAuth } from '../../../contexts/AuthContext';
import MainLayout from '../../../components/layout/MainLayout';
import AnswerOptionButton from '../../../components/quiz/AnswerOptionButton';
import LiveScoreboard from '../../../components/quiz/live/LiveScoreboard';
import PodiumFinish from '../../../components/quiz/live/PodiumFinish';
import LiveQuizLobby from '../../../components/quiz/live/LiveQuizLobby';
import RatingRevealCard from '../../../components/quiz/live/RatingRevealCard';

/**
 * Synchronised live quiz play.
 *
 * Unlike the self-paced page, nothing here advances on the client: the phase and
 * its deadline both come from the server, and the countdown is derived from
 * `remainingMs`, which the hook corrects for clock skew. Every student in the
 * room therefore sees the same question and the same clock.
 *
 * Wrapped in the normal student shell — deliberately no focus mode. Without
 * the header a student stuck mid-room has no way back out.
 */
const LiveQuizPlayPage: React.FC = () => {
  const { roomId: roomIdParam } = useParams<{ roomId: string }>();
  const roomId = roomIdParam ? Number(roomIdParam) : null;
  const theme = useTheme();
  const { authState } = useAuth();
  const user = authState.user;

  const { state, mode, error, remainingMs, refresh } = useLiveQuizRoom(roomId);
  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joining, setJoining] = useState(true);

  // Enter the room once, on mount.
  useEffect(() => {
    if (roomId === null) {
      return;
    }

    joinRoom(roomId)
      .then(() => setJoinError(null))
      .catch(err => {
        setJoinError(
          err?.response?.data?.code === 'JOIN_WINDOW_CLOSED'
            ? 'This quiz has already moved on past the point where you can join.'
            : 'Could not join the quiz.'
        );
      })
      .finally(() => setJoining(false));
  }, [roomId]);

  // Clear the local selection whenever the room moves to a new question.
  const questionId = state?.question?.id;
  useEffect(() => {
    setSelectedOptionId(null);
  }, [questionId]);

  const hasAnswered = state?.viewer?.hasAnswered || selectedOptionId !== null;
  const secondsLeft = remainingMs === null ? null : Math.ceil(remainingMs / 1000);

  const handleAnswer = async (optionId: number) => {
    if (roomId === null || !state?.question || hasAnswered) {
      return;
    }

    // Optimistic: the tile locks immediately so the student is not left
    // wondering whether the tap registered on a 20-second clock.
    setSelectedOptionId(optionId);

    try {
      const result = await submitLiveAnswer(roomId, state.question.id, optionId);
      if (!result.accepted) {
        // The question moved on, or it was already answered — release the tile
        // rather than leaving it looking locked in.
        setSelectedOptionId(null);
      }
    } catch {
      setSelectedOptionId(null);
    }
  };

  const progress = useMemo(() => {
    if (!state || state.totalQuestions === 0) return 0;
    return ((state.currentQuestionIndex + 1) / state.totalQuestions) * 100;
  }, [state]);

  // Nothing has happened yet during scheduled/lobby — the question count and
  // progress bar are only meaningful once the room has moved past the lobby.
  const hasStarted = state ? state.status !== 'scheduled' && state.status !== 'lobby' : false;

  if (roomId === null) {
    return (
      <MainLayout>
        <Alert severity="error">No quiz specified.</Alert>
      </MainLayout>
    );
  }

  if (joinError) {
    return (
      <MainLayout>
        <Alert severity="warning" sx={{ borderRadius: 3 }}>
          {joinError}
        </Alert>
      </MainLayout>
    );
  }

  if (!state) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Header: progress, live indicator, participant count */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
            theme.palette.secondary.main,
            0.1
          )} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mb: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WifiTetheringIcon
              sx={{
                color: mode === 'stream' ? theme.palette.success.main : theme.palette.warning.main,
                fontSize: 20
              }}
            />
            <Typography variant="body2" fontWeight={600}>
              {mode === 'stream' ? 'Live' : mode === 'polling' ? 'Live (backup connection)' : 'Connecting…'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <GroupIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {state.participantCount} playing
            </Typography>
          </Box>
        </Box>

        {/* Nothing has started yet during scheduled/lobby — a question count
            and progress bar for a quiz that hasn't begun is just noise. */}
        {hasStarted && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Question {Math.max(state.currentQuestionIndex + 1, 1)} of {state.totalQuestions}
              </Typography>
              {secondsLeft !== null && state.status === 'question' && (
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    color:
                      secondsLeft > 10
                        ? theme.palette.success.main
                        : secondsLeft > 5
                          ? theme.palette.warning.main
                          : theme.palette.error.main
                  }}
                >
                  {secondsLeft}s
                </Typography>
              )}
            </Box>

            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                '& .MuiLinearProgress-bar': {
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  borderRadius: 4
                }
              }}
            />
          </>
        )}
      </Paper>

      {error && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 3 }}>
          {error}
        </Alert>
      )}

      <AnimatePresence mode="wait">
        {/* Waiting room: split scheduled (too early, no roster yet) from
            lobby (roster, "you're in" reassurance, countdown to start). */}
        {(state.status === 'scheduled' || state.status === 'lobby') && (
          <motion.div key="lobby" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <LiveQuizLobby
              state={state}
              remainingMs={remainingMs}
              currentUserId={user?.id}
              joining={joining}
              onExpire={refresh}
            />
          </motion.div>
        )}

        {/* Live question and answer reveal */}
        {(state.status === 'question' || state.status === 'reveal') && state.question && (
          <motion.div
            key={`q-${state.question.id}-${state.status}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 4,
                mb: 3,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper'
              }}
            >
              <Typography variant="h5" fontWeight={600} sx={{ lineHeight: 1.6 }}>
                {state.question.text}
              </Typography>
            </Paper>

            <Grid container spacing={2}>
              {state.question.options.map((option, index) => (
                <Grid item xs={12} sm={6} key={option.id}>
                  <AnswerOptionButton
                    option={{
                      id: option.id,
                      questionId: state.question?.id ?? 0,
                      optionText: option.text,
                      isCorrect: option.isCorrect ?? false,
                      optionOrder: index + 1
                    }}
                    optionIndex={index}
                    selected={selectedOptionId === option.id}
                    // is_correct only exists once the server reveals it.
                    showFeedback={state.status === 'reveal'}
                    isCorrect={option.isCorrect}
                    disabled={state.status === 'reveal' || hasAnswered}
                    onClick={() => handleAnswer(option.id)}
                  />
                </Grid>
              ))}
            </Grid>

            {hasAnswered && state.status === 'question' && (
              <Typography sx={{ mt: 3, textAlign: 'center' }} color="text.secondary">
                Answer locked in — waiting for everyone else…
              </Typography>
            )}
          </motion.div>
        )}

        {/* Between-question standings */}
        {state.status === 'scoreboard' && state.scoreboard && (
          <motion.div key="scoreboard" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <LiveScoreboard entries={state.scoreboard} currentUserId={user?.id} />
          </motion.div>
        )}

        {/* Podium — renders immediately and never waits on the rating fetch. */}
        {state.status === 'finished' && state.scoreboard && (
          <motion.div key="finished" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <PodiumFinish entries={state.scoreboard} currentUserId={user?.id} />
            <Box sx={{ mt: 3 }}>
              <RatingRevealCard roomId={roomId} />
            </Box>
            <Box sx={{ mt: 3 }}>
              <LiveScoreboard
                entries={state.scoreboard}
                currentUserId={user?.id}
                title="Full results"
              />
            </Box>
          </motion.div>
        )}

        {state.status === 'abandoned' && (
          <Alert severity="info" sx={{ borderRadius: 3 }}>
            This quiz was stopped by your teacher.
          </Alert>
        )}
      </AnimatePresence>
    </MainLayout>
  );
};

export default LiveQuizPlayPage;
